import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import SelectAllWithActionIcon from './SelectAllWithActionIcon';
import AddMultipleRow from './AddMultipleRow';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  IconButton,
  makeStyles,
  Box,
  Tooltip,
  Badge,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  alertsActions,
  curBookingsActions,
  showOverlayActions,
} from 'store/actions';
import { bookingErrorStatuses } from 'store/models/bookings/Booking';
import Bookings from 'store/models/bookings/Bookings';
import BookingNoInput from './BookingNoInput';
import { ticketDataSelector } from 'store/selectors';
import { curBookingsBookingsSelector } from 'store/selectors/cur-bookings.selector';
import {
  canEditTicketSelector,
  ticketBookingNosFromEmailSelector,
} from 'store/selectors/ticket.selector';
import { AttachFileRounded } from '@material-ui/icons';
import { MatUiCheckbox } from '.';
import { isEmpty } from 'lodash';

interface MainBookingsProps {}

const useStyles = makeStyles((theme) => ({
  clickToAttachButton: {
    background: 'rgba(198, 47, 133, 0.04)',
  },
}));

const MainBookings: React.FC<MainBookingsProps> = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const bookings = useSelector(curBookingsBookingsSelector);
  const ticketData = useSelector(ticketDataSelector);
  const bookingNosFromEmail = useSelector(
    ticketBookingNosFromEmailSelector,
  );

  const canEdit = useSelector(canEditTicketSelector);

  useEffect(() => {
    console.debug(`MainBookings: bookings changed `, bookings);
  }, [bookings]);

  useEffect(() => {
    console.debug(`ticketData changed: `, ticketData);
  }, [ticketData]);

  const bookingsM = useMemo(
    () => bookings && new Bookings(bookings),
    [bookings],
  );

  const handleClickToAddNewRow = () => {
    const newBookingsM: Bookings = dispatch(
      curBookingsActions.addNewBooking(isSelectedAll),
    );
    if (newBookingsM) {
      setIsSelectedAll(newBookingsM.isAllSelected());
    }
  };

  /**
   * no validation
   * @param index
   * @param value
   */
  const handleBookingNoChange = (index, value) => {
    dispatch(curBookingsActions.updateBookingNoAtIndex(index, value));
  };

  /**
   * with validation
   * TODO:
   * @param index
   * @param value
   */
  const handleBookingNoBlur = (index, value) => {
    dispatch(
      curBookingsActions.updateBookingNoAtIndexAndValidate(
        index,
        value,
        true,
      ),
    );
  };

  const [isSelectedAll, setIsSelectedAll] = useState(false);

  const handleToggleSelectAll = (checked: boolean) => {
    setIsSelectedAll(checked);
    dispatch(curBookingsActions.toggleSelectAllBookings(checked));
  };

  const handleToggleSelectBooking = (index, checked) => {
    const newBookingsM: Bookings = dispatch(
      curBookingsActions.toggleSelectBooking(index, checked),
    );
    setIsSelectedAll(newBookingsM.isAllSelected());
  };

  const handleRemoveBookingAtIndex = (index: number) => {
    const newBookingsM: Bookings = dispatch(
      curBookingsActions.removeBookingAtIndex(index),
    );
    // recheck to update isSelectedAll
    setIsSelectedAll(newBookingsM.isAllSelected());
  };

  const handleRemoveSelectedBookings = () => {
    const newBookingsM: Bookings = dispatch(
      curBookingsActions.removeSelectedBookings(),
    );
    // recheck to update isSelectedAll
    setIsSelectedAll(newBookingsM.isAllSelected());
  };

  const handleClickAttachAtBooking = (bookingIndex: number) => {
    // check if booking no is empty
    if (!bookingsM) {
      return;
    }
    if (bookingsM.isBookingNoEmptyAtIndex(bookingIndex)) {
      dispatch(
        alertsActions.error(
          bookingErrorStatuses[`bookingNoIsRequired`],
        ),
      );
    } else {
      dispatch(
        curBookingsActions.setViewingBookingIndex(bookingIndex),
      );
      dispatch(showOverlayActions.show('FileSelection'));
    }
  };

  const handleUseAllBookingsFromEmail = async () => {
    const newBookingsM: Bookings = await dispatch(
      curBookingsActions.useAllBookingsFromEmail(
        // // ********** related to booking/info **********
        true,
        isSelectedAll,
      ),
    );
    setIsSelectedAll(newBookingsM.isAllSelected());
  };

  function indexesHasDuplicateBookingNoWithIndex(
    index,
    bookings,
    bookingNo,
  ): number[] {
    if (index < 0 || index >= bookings.length)
      throw Error(`invalid index ${index}`);
    return bookings
      .map((bn, i) => {
        return i !== index &&
          !isEmpty(bn.booking.bookingNo) &&
          bn.booking.bookingNo === bookingNo
          ? i
          : undefined;
      })
      .filter((bn) => bn !== undefined) as number[];
  }

  function markHasErrorToIndexes(indexes: number[], bookings) {
    bookings.map((b, i) =>
      indexes.includes(i) ? b.markHasError('bookingNoIsDup') : b,
    );
  }

  function checkDuplicateBookingNo() {
    const arrBookings = bookingsM.bookingMs;
    if (arrBookings && arrBookings.length) {
      const uniqueArr: any[] = [];
      for (const item of arrBookings) {
        if (
          !uniqueArr.some(
            (x) => x.booking.bookingNo === item.booking.bookingNo,
          ) &&
          item.hasErrors &&
          Object.values(item.hasErrors).every((x) => !x)
        ) {
          uniqueArr.push(item);
        }
      }
      uniqueArr.forEach((item, index) => {
        const hasDupIndexes = indexesHasDuplicateBookingNoWithIndex(
          index,
          arrBookings,
          item.booking.bookingNo,
        );
        if (hasDupIndexes.length) {
          markHasErrorToIndexes(
            hasDupIndexes.concat([index]),
            arrBookings,
          );
        }
      });
    }
  }
  checkDuplicateBookingNo();
  return (
    <SMainBookingsWrapper>
      <Box
        display="flex"
        justifyContent="space-between"
        paddingRight="3.8rem"
        marginBottom="0.3rem"
      >
        <SelectAllWithActionIcon
          handleToggleSelectAll={handleToggleSelectAll}
          handleClickActionMany={handleRemoveSelectedBookings}
          isActioningMany={false}
          isSelectedAll={isSelectedAll}
          disabled={!canEdit}
          isDelete={true}
        />
        <SBookingsRight>
          {!!bookingNosFromEmail.length && (
            <Tooltip title={`${bookingNosFromEmail}`}>
              <Button
                color="primary"
                onClick={handleUseAllBookingsFromEmail}
                disabled={!canEdit}
              >
                {`Use all booking no. from email (${bookingNosFromEmail.length})`}
              </Button>
            </Tooltip>
          )}
          <AddMultipleRow
            isSelectedAll={isSelectedAll}
            setIsSelectedAll={setIsSelectedAll}
          />
        </SBookingsRight>
      </Box>
      <SBookingsList>
        {bookingsM.bookingMs.map((bookingM, index) => {
          const booking = bookingM.booking;
          const fileSelectedCount = booking.fileSelected?.length;
          const hasFileSelected = (fileSelectedCount ?? 0) > 0;
          return (
            <SBox
              position="relative"
              paddingBottom="1.2rem"
              key={`${booking.bookingNo}-${index}`}
            >
              <SEachBooking>
                <MatUiCheckbox
                  value={booking.isSelected}
                  handleChange={(checked: boolean) =>
                    handleToggleSelectBooking(index, checked)
                  }
                  disabled={!canEdit}
                />
                {/* <MainTextInput
                  // name={`bookingNo${index}`}
                  value={booking.bookingNo}
                  handleOnBlur={(value) =>
                    handleBookingNoChange(index, value)
                  }
                /> */}
                <BookingNoInput
                  index={index}
                  verticalDir="top"
                  handleValueChanged={(value) =>
                    handleBookingNoChange(index, value)
                  }
                  bookingM={bookingM}
                  handleOnBlur={(value) =>
                    handleBookingNoBlur(index, value)
                  }
                  disabled={!canEdit}
                />

                <SClickToAttachButton
                  variant="outlined"
                  color="primary"
                  className={classes.clickToAttachButton}
                  onClick={() => handleClickAttachAtBooking(index)}
                  disabled={!canEdit}
                  startIcon={
                    hasFileSelected ? (
                      <Badge
                        badgeContent={fileSelectedCount}
                        color="primary"
                      >
                        <AttachFileRounded />
                      </Badge>
                    ) : null
                  }
                >
                  Click To Attach
                </SClickToAttachButton>
                <IconButton
                  color="primary"
                  onClick={() => handleRemoveBookingAtIndex(index)}
                  disabled={!canEdit}
                >
                  <DeleteIcon />
                </IconButton>
              </SEachBooking>
            </SBox>
          );
        })}
        <Box display="flex">
          <NewBookingButton
            onClick={() => handleClickToAddNewRow()}
            disabled={!canEdit}
          ></NewBookingButton>
        </Box>
      </SBookingsList>
    </SMainBookingsWrapper>
  );
};

export default MainBookings;
const SBox = styled(Box)``;
const SMainBookingsWrapper = styled.div``;
const SBookingsList = styled.div``;
const SEachBooking = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: min-content auto auto min-content;
  grid-column-gap: 9px;
  & .MuiCheckbox-root {
    padding: 0;
    margin-left: 9px;
  }
  & .MuiFormControlLabel-root {
    margin-right: 0;
  }
  & + & {
    margin-top: 0.5rem;
  }
`;

const useNewBookingButtonStyles = makeStyles((theme) => ({
  root: {
    borderStyle: 'dashed',
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(7),
    flex: 1,
  },
}));

// Inspired by blueprintjs
const NewBookingButton = (props) => {
  const classes = useNewBookingButtonStyles();
  return (
    <Button
      className={classes.root}
      variant="outlined"
      color="primary"
      {...props}
    >
      Click to add new row
    </Button>
  );
};

const SBookingsRight = styled.div`
  display: flex;
  column-gap: 0.5rem;
`;
const SClickToAttachButton = styled(Button)`
  & .MuiButton-startIcon {
    position: absolute;
    left: 1rem;
  }
`;
